import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormTextField } from '@/components/Form/FormTextField/FormTextField';
import { EntityType } from '@/core/graphql/generated/types';
import { Permission } from '@/core/store/auth/auth.types';
import { useHasPermission } from '@/core/store/auth/authSelectors';

import { useDcrFields } from '../../_constants/useDcrFields';
import { useDcrContext } from '../../_context/DcrContext';
import type { ContactSectionValuesIn as HcoContactSectionValuesIn } from '../../_schemas/HcoSchemas/ContactSection.validation';
import {
	EMAIL_FIELD_KEYS,
	type ContactSectionValuesIn as HcpContactSectionValuesIn
} from '../../_schemas/HcpSchemas/ContactSection.validation';
import { DcrFormController } from '../../DcrFormController/DcrFormController';
import { FormSection } from '../../DcrModal/FormSection/FormSection';

type Props = {
	entityType?: EntityType;
};

type EmailFieldName = (typeof EMAIL_FIELD_KEYS)[number];
type EmailDefaults = Partial<Record<EmailFieldName, string | null | undefined>>;
type EmailDirtyFields = Partial<Record<EmailFieldName, boolean>>;

const countVisibleEmails = (defaults: EmailDefaults | undefined) => {
	let lastPopulatedIndex = -1;
	EMAIL_FIELD_KEYS.forEach((key, index) => {
		if (defaults?.[key]) lastPopulatedIndex = index;
	});
	return Math.max(lastPopulatedIndex + 1, 1);
};

export const ContactSection = ({ entityType = EntityType.Hcp }: Props) => {
	const { t } = useTranslation();
	const { hcpFields, hcoFields } = useDcrFields();
	const {
		control,
		setValue,
		getValues,
		trigger,
		formState: { dirtyFields, defaultValues }
	} = useFormContext<HcoContactSectionValuesIn | HcpContactSectionValuesIn>();
	const emailDirtyFields = dirtyFields as EmailDirtyFields;
	const hasUsEmailPermission = useHasPermission(Permission.UsEmails);
	const { entityCountryId } = useDcrContext();
	const section = 'contact';

	const hasEmailAccess =
		entityType !== EntityType.Hcp ||
		hasUsEmailPermission ||
		entityCountryId !== 'us';

	const allowMultipleEmails =
		entityType === EntityType.Hcp && entityCountryId === 'us';

	const [visibleEmails, setVisibleEmails] = useState(() =>
		countVisibleEmails(defaultValues)
	);

	// our fields are uncontrolled input fields
	// so we need to force a re-render when we remove an email field due to value shifting logic.
	const [resetToken, setResetToken] = useState(0);

	// react-hook-form + zodResolver only auto-revalidate the field that was
	// just edited, not other fields whose validity depends on it
	// we have validation logic that depends on other email fields so we trigger validation manually
	const handleEmailChange =
		(onChange: (value: string) => void) => (value: string) => {
			onChange(value);
			trigger(EMAIL_FIELD_KEYS);
		};

	const handleAddEmail = () => {
		setVisibleEmails(count => Math.min(count + 1, EMAIL_FIELD_KEYS.length));
	};

	const handleRemoveEmail = (index: number) => {
		setResetToken(token => token + 1);

		if (visibleEmails <= 1) {
			setValue(EMAIL_FIELD_KEYS[0], '', { shouldDirty: true });
			trigger(EMAIL_FIELD_KEYS);
			return;
		}

		// Shift later values up so the visible slots stay contiguous with the
		for (let i = index; i < visibleEmails - 1; i++) {
			const nextValue = getValues(EMAIL_FIELD_KEYS[i + 1]) ?? '';
			setValue(EMAIL_FIELD_KEYS[i], nextValue, { shouldDirty: true });
		}
		setValue(EMAIL_FIELD_KEYS[visibleEmails - 1], '', { shouldDirty: true });
		setVisibleEmails(count => Math.max(count - 1, 1));
		trigger(EMAIL_FIELD_KEYS);
	};

	return (
		<FormSection title={t('Contact')} id={section}>
			{/* {entityType === EntityType.Hcp && (
				<DcrFormController
					name='phone2_V'
					section={section}
					control={control}
					render={({ field, fieldState }) => (
						<FormTextField
							id='phone2_V'
							name={field.name}
							label={hcpFields.phone2_V.label}
							errorMessage={fieldState.error?.message}
							indicateError={fieldState.error ? true : false}
							defaultValue={(field.value ?? '') as string}
							onChange={field.onChange}
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							isDirty={!!(dirtyFields as any).phone2_V}
						/>
					)}
				/>
			)} */}
			{/* Note(Filip): As of 28.01.2026 - Jonas reqested this to be hidden for the time being */}
			{/* <DcrFormController
					name='officePhoneCda_V'
					section={section}
					control={control}
					render={({ field, fieldState }) => (
						<FormTextField
							id='officePhoneCda_V'
							name={field.name}
							label='Office Phone'
							indicateError={fieldState.error ? true : false}
							errorMessage={fieldState.error?.message}
							defaultValue={(field.value ?? '') as string}
							onChange={field.onChange}
							isDirty={!!dirtyFields.officePhoneCda_V}
						/>
					)}
				/> */}

			{/* Note(Filip): As of 27.01.2026 - Jonas reqested this to be hidden for the time being */}
			{/* <DcrFormController
					name='faxCda_V'
					section={section}
					control={control}
					render={({ field, fieldState }) => (
						<FormTextField
							id='faxCda_V'
							name={field.name}
							label='Fax'
							indicateError={fieldState.error ? true : false}
							errorMessage={fieldState.error?.message}
							defaultValue={(field.value ?? '') as string}
							onChange={field.onChange}
							isDirty={!!dirtyFields.faxCda_V}
						/>
					)}
				/> */}

			{hasEmailAccess &&
				allowMultipleEmails &&
				EMAIL_FIELD_KEYS.slice(0, visibleEmails).map((name, index) => (
					<DcrFormController
						key={`${name}-${visibleEmails}-${resetToken}`}
						name={name}
						section={section}
						control={control}
						render={({ field, fieldState }) => (
							<FormTextField
								id={name}
								name={field.name}
								label={index === 0 ? t('Email', { count: visibleEmails }) : ''}
								placeholder={t('Email {{number}}', { number: index + 1 })}
								indicateError={!!fieldState.error}
								errorMessage={fieldState.error?.message}
								defaultValue={field.value ?? ''}
								onChange={handleEmailChange(field.onChange)}
								isDirty={!!emailDirtyFields[name]}
								canBeRemoved={visibleEmails > 1 || !!field.value}
								onRemove={() => handleRemoveEmail(index)}
								canAddNewItem={
									index === visibleEmails - 1 &&
									visibleEmails < EMAIL_FIELD_KEYS.length
								}
								onAddNewItemClick={handleAddEmail}
							/>
						)}
					/>
				))}

			{hasEmailAccess && !allowMultipleEmails && (
				<DcrFormController
					name='emailCda_V'
					section={section}
					control={control}
					render={({ field, fieldState }) => (
						<FormTextField
							id='emailCda_V'
							name={field.name}
							label={
								entityType === EntityType.Hcp
									? hcpFields.emailCda_V.label
									: hcoFields.emailCda_V.label
							}
							indicateError={!!fieldState.error}
							errorMessage={fieldState.error?.message}
							defaultValue={field.value ?? ''}
							onChange={field.onChange}
							isDirty={!!dirtyFields.emailCda_V}
						/>
					)}
				/>
			)}

			<DcrFormController
				name='websiteCda_V'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormTextField
						id='websiteCda_V'
						name={field.name}
						label={
							entityType === EntityType.Hcp
								? hcpFields.websiteCda_V.label
								: hcoFields.websiteCda_V.label
						}
						indicateError={fieldState.error ? true : false}
						errorMessage={fieldState.error?.message}
						defaultValue={field.value ?? ''}
						onChange={field.onChange}
						isDirty={!!dirtyFields.websiteCda_V}
					/>
				)}
			/>
		</FormSection>
	);
};
